<style lang="stylus" scoped>
@media (max-width: 365px) {
  /* line 18, ../scss/bootstrap/mixins/_responsive-visibility.scss */
  .mobileDevice {
    padding-left: 40px !important;
  }
}

@media (min-width: 366px) {
  .largeScreen {
    display: flex !important;
    justify-content: center !important;
  }
}
</style>

<style scoped>
.unenroll-grid {
  margin: -12px !important;
}

.unenroll-grid .v-col {
  padding: 12px !important;
}

.unenroll-cta {
  height: 48px !important;
}
</style>

<template>
  <div style="position: relative">
    <v-card-title
      class="font-weight-bold px-4 py-3 d-flex align-center justify-space-between"
      style="background-color: #fec627; position: sticky; top: 0; margin-top: 0; z-index: 999; font-size: 20px"
    >
      <div v-if="isCartRemoval">Remove course {{ (courseInfo || {})._coursePrefixDisplayLabel }}?</div>
      <div v-else>Unenroll from {{ (courseInfo || {})._coursePrefixDisplayLabel }}?</div>
      <v-icon @click="closeModal()" color="black" size="28" style="cursor: pointer">mdi-close</v-icon>
    </v-card-title>

    <v-container class="pa-4">
      <v-row wrap class="unenroll-grid">
        <v-col cols="12">
          <div class="text-body-2">
            <div v-if="isCartRemoval">Are you sure you'd like to remove this course?</div>
            <div v-else>Are you sure you'd like to unenroll from this course?</div>
          </div>
        </v-col>
        <!-- Learner eligible for a refund -->
        <v-col cols="12" v-if="!isCartRemoval && isSelfPaid && isRefundEligible && !isCourseRefunded">
          <div class="text-body-2">
            <v-icon style="font-size: 18px; padding-bottom: 1px" class="material-icons" color="black"
              >mdi-information</v-icon
            >
            If you unenroll, you will be automatically issued a refund for this transaction. Please allow 3-5 business
            days to see this refund reflected on your account. Auto refunds will be issued one time only.
          </div>
        </v-col>
        <!-- Learner NOT eligible for a refund -->
        <v-col cols="12" v-else-if="!isCartRemoval && isSelfPaid && !isRefundEligible && !validEMCheck">
          <div class="text-body-2">
            <v-icon style="font-size: 18px; padding-bottom: 1px" class="material-icons" color="black"
              >mdi-information</v-icon
            >
            If you unenroll, you will not be eligible for a course refund, as the drop deadline has already passed. For
            more information, please see the
            <a
              href="https://ulc.asu.edu/terms-of-use/"
              class="font-weight-bold"
              style="color: black; border-bottom: 2px solid #ffc627"
              target="_blank"
              rel="noopener"
            >
              ULC Terms of Use.
            </a>
          </div>
        </v-col>
        <v-col
          cols="12"
          class="d-flex"
          :class="[$vuetify.display.xs ? 'flex-column' : 'justify-end']"
          style="gap: 12px"
        >
          <UiButton variant="dark-outline" size="medium" class="unenroll-cta" @click="closeModal()">
            No, go back
          </UiButton>
          <UiButton
            v-if="!isCartRemoval"
            class="unenroll-cta"
            @click="
              doUnenroll();
              captureEcomDataLayer('refund');
            "
            variant="gold"
            size="medium"
          >
            Yes, unenroll
          </UiButton>
          <UiButton
            v-else
            class="unenroll-cta"
            @click="
              doUnenroll();
              captureEcomDataLayer('remove_from_cart');
              closeDialog();
            "
            variant="gold"
            size="medium"
          >
            Yes, remove
          </UiButton>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

import { Logger } from '../mixins/logger';
import { AjaxLoader } from '../mixins/ajaxLoader';
import { DataLayerMixin } from '../mixins/dataLayerMixin.js';
import { getAttributionParams } from '@/util/param-persistence';
import { buildGa4EcommerceItem } from '@/utils/ga4Items';
import { Button as UiButton } from './ui/index.js';

export default defineComponent({
  emits: ['doCourseUnenroll', 'closeDialog', 'isSuccessfulUnenroll'],
  components: {
    UiButton
  },

  data() {
    return {
      disableClose: false
    };
  },

  name: 'PreUnenrollment',

  props: {
    courseInfo: Object,
    isRefundEligible: Boolean,
    paymentMethod: String,
    buttonAction: String,
    enrollmentMode: String,
    isCourseRefunded: Boolean,
    verifiedTransactionId: String
  },

  mixins: [Logger, AjaxLoader, DataLayerMixin],

  computed: {
    // Refund notes only apply to learners who paid themselves — these are the methods the
    // unenroll interceptor auto-refunds. Partner-covered seats get the plain confirmation.
    isSelfPaid() {
      return ['paypal', 'stripe'].includes((this.paymentMethod || '').toLowerCase());
    },

    // A seat that was never charged: still in checkout, or honor-track. Drives the
    // remove-vs-unenroll copy and the CTA, and suppresses both refund notes.
    isCartRemoval() {
      return this.buttonAction === 'Enroll and pay' || this.enrollmentMode === 'honor';
    },

    validEMCheck() {
      return ['honor', 'audit'].indexOf((this.courseInfo || {})._enrollmentMode || '') > -1;
    }
  },

  methods: {
    captureEcomDataLayer(event) {
      // Clear previous ecommerce object
      if (window.dataLayer) {
        window.dataLayer.push({ ecommerce: null });
      }

      const info = this.courseInfo || {};
      const { campaign, partner } = getAttributionParams();

      //Ecom data layer
      const ecom = {
        event: event,
        campaign,
        partner,
        ecommerce: {
          currency: 'USD',
          value: 25.0,
          ...(event === 'refund' && {
            transaction_id: this.verifiedTransactionId
          }),
          items: [
            buildGa4EcommerceItem({
              canvasCourseId: info._canvasCourseId,
              offeringId: info._courseOfferingId,
              courseName: info._courseName,
              coursePrefix: info._coursePrefix,
              startDate: info._courseStartDate,
              courseType: info._courseType,
              price: 25.0
            })
          ]
        }
      };

      this.asuoDataLayerClick('ecommerceTags', '0', ecom);
    },
    doUnenroll() {
      this.logInfo('doUnenroll');

      this.$emit('doCourseUnenroll', {
        courseName: this.courseInfo._courseName,
        courseStartDate: this.courseInfo._courseStartDate
      });
    },

    closeDialog() {
      this.$emit('closeDialog');
    },
    closeModal() {
      this.$emit('closeDialog');
      setTimeout(() => {}, 500);
    },

    switchToPostUnenrollment() {
      this.$emit('isSuccessfulUnenroll');
    }
  }
});
</script>
